import type { RenderStateColorGradient, RGBA } from "@novorender/api";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useLazyGetDeviationProfilesQuery } from "apis/dataV2/dataV2Api";
import type { ColorStop, DeviationProjectConfig } from "apis/dataV2/deviationTypes";
import { Permission } from "apis/dataV2/permissions";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectAlignmentIds } from "features/alignment/selectors";
import type { AlignmentId } from "features/alignment/types";
import { renderActions, selectPoints, selectViewMode } from "features/render";
import { useAbortController } from "hooks/useAbortController";
import { useCheckProjectPermission } from "hooks/useCheckProjectPermissions";
import { useSceneId } from "hooks/useSceneId";
import { selectProjectIsV2 } from "slices/explorer";
import { telemetry } from "telemetry";
import { AsyncStatus, ViewMode } from "types/misc";
import { getAssetUrl } from "utils/misc";

import { deviationsActions } from "./deviationsSlice";
import type { UiDeviationConfig, UiDeviationProfile } from "./deviationTypes";
import { DeviationType } from "./deviationTypes";
import { useHighlightDeviation } from "./hooks/useHighlightDeviation";
import { useSetCenterLineAlignment } from "./hooks/useSetCenterLineAlignment";
import { useSyncWithAlignment } from "./hooks/useSyncWithAlignment";
import {
    selectDeviationProfiles,
    selectNumPointDeviations,
    selectSelectedProfile,
    selectSelectedProfileId,
} from "./selectors";
import { accountForAbsValues, MAX_DEVIATION_PROFILE_COUNT } from "./utils";

const EMPTY_ARRAY: ColorStop[] = [];

export function useHandleDeviations() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const { t } = useTranslation();
    const isProjectV2 = useAppSelector(selectProjectIsV2);
    const sceneId = useSceneId();
    const numPointDeviations = useAppSelector(selectNumPointDeviations);
    const profiles = useAppSelector(selectDeviationProfiles);
    const selectedProfileId = useAppSelector(selectSelectedProfileId);
    const dispatch = useAppDispatch();
    const points = useAppSelector(selectPoints);
    const defaultColorStops = points.deviation.colorGradient.knots ?? EMPTY_ARRAY;
    const profile = useAppSelector(selectSelectedProfile);
    const [abortController] = useAbortController();
    const active = useAppSelector(selectViewMode) === ViewMode.Deviations;
    const alignmentIds = useAppSelector(selectAlignmentIds);
    const checkProjectPermission = useCheckProjectPermission();

    const [getDeviationProfiles] = useLazyGetDeviationProfilesQuery();

    useSetCenterLineAlignment();
    useHighlightDeviation();
    useSyncWithAlignment();

    useEffect(() => {
        initDeviationProfiles();

        async function initDeviationProfiles() {
            if (
                !view ||
                !sceneId ||
                profiles.status !== AsyncStatus.Initial ||
                alignmentIds.status !== AsyncStatus.Success ||
                !checkProjectPermission(Permission.DeviationRead) ||
                numPointDeviations === undefined
            ) {
                return;
            }

            dispatch(deviationsActions.setProfiles({ status: AsyncStatus.Loading }));

            try {
                const url = (await getAssetUrl(view, "deviations.json")).toString();

                // When we run the calculation - saved config is copied to another storage where calculator picks it up, so
                // savedConfig - the one where we save changes
                // commitedConfig - the one picked up (and later updated!) by the service
                const [commitedConfig, savedConfig] = await Promise.all([
                    numPointDeviations > 0
                        ? fetch(url).then((res) => {
                              if (!res.ok) {
                                  return;
                              }

                              return res.json() as Promise<DeviationProjectConfig>;
                          })
                        : undefined,
                    isProjectV2
                        ? getDeviationProfiles({ projectId: sceneId })
                              .unwrap()
                              .then((data) => (data ? configToUi(data, defaultColorStops) : undefined))
                        : undefined,
                ]);

                if (commitedConfig && savedConfig) {
                    // Commited config always has relevant runData
                    savedConfig.runData = commitedConfig.runData;
                }

                let config: UiDeviationConfig;
                if (commitedConfig && savedConfig) {
                    config = savedConfig;
                    matchProfileIndexes(savedConfig, commitedConfig);
                } else if (commitedConfig) {
                    config = configToUi(commitedConfig, defaultColorStops);
                    config.profiles.forEach((p, i) => (p.index = i));
                } else if (savedConfig) {
                    config = savedConfig;
                } else {
                    config = getEmptyDeviationConfig();
                }
                config = withCenterLineObjectIds(config, alignmentIds.data);

                dispatch(
                    deviationsActions.setProfiles({
                        status: AsyncStatus.Success,
                        data: config,
                    }),
                );

                dispatch(
                    deviationsActions.initFromProfileIndex({
                        index: points.deviation.index,
                    }),
                );
            } catch (e) {
                console.warn(e);
                telemetry.trackCaughtException({
                    source: "deviations.useHandleDeviations.initDeviationProfiles",
                    category: "data",
                    error: e,
                });
                dispatch(
                    deviationsActions.setProfiles({
                        status: AsyncStatus.Error,
                        msg: t("loadingDeviationsError"),
                    }),
                );
            }
        }
    }, [
        dispatch,
        view,
        isProjectV2,
        sceneId,
        profiles,
        defaultColorStops,
        getDeviationProfiles,
        points.deviation.index,
        abortController,
        selectedProfileId,
        alignmentIds,
        checkProjectPermission,
        t,
        numPointDeviations,
    ]);

    // Set current deviation and colors
    useEffect(() => {
        if (profile && active) {
            let colorStops = profile.colors.colorStops.slice();
            if (profile.colors.absoluteValues) {
                colorStops = accountForAbsValues(colorStops);
            }
            dispatch(
                renderActions.setPoints({
                    deviation: {
                        index: profile.index,
                        colorGradient: {
                            knots: colorStops,
                        },
                    },
                }),
            );
        }
    }, [dispatch, profile, active]);

    // Set deviation colors
    useEffect(() => {
        if (profiles.status !== AsyncStatus.Success || !view) {
            return;
        }

        const colorGradients: RenderStateColorGradient<RGBA>[] = new Array(MAX_DEVIATION_PROFILE_COUNT).fill({
            knots: [],
        } as RenderStateColorGradient<RGBA>);

        profiles.data.profiles.forEach((profile, index) => {
            if (index < 0 || index >= colorGradients.length) {
                return;
            }

            let knots = profile.colors.colorStops.slice();
            if (profile.colors.absoluteValues) {
                knots = accountForAbsValues(knots);
            }

            colorGradients[profile.index] = {
                knots: knots.toSorted((a, b) => a.position - b.position),
            };
        });

        view.modifyRenderState({
            points: { deviation: { colorGradients } },
        });
    }, [view, profiles]);
}

function getEmptyDeviationConfig(): UiDeviationConfig {
    return {
        version: "1.0",
        rebuildRequired: false,
        profiles: [],
    };
}

function withCenterLineObjectIds(config: UiDeviationConfig, alignments: AlignmentId[]): UiDeviationConfig {
    return {
        ...config,
        profiles: config.profiles.map((profile) => {
            if (!profile.subprofiles.some((sp) => sp.centerLine)) {
                return profile;
            }

            return {
                ...profile,
                subprofiles: profile.subprofiles.map((sp) => {
                    if (!sp.centerLine) {
                        return sp;
                    }

                    return {
                        ...sp,
                        centerLine: {
                            ...sp.centerLine,
                            objectId: alignments.find((p) => p.brepId === sp.centerLine!.brepId)?.id ?? 0,
                        },
                    };
                }),
            };
        }),
    };
}

function configToUi(config: DeviationProjectConfig, defaultColorStops: ColorStop[]): UiDeviationConfig {
    const defaultColors = {
        absoluteValues: false,
        colorStops: defaultColorStops,
    };

    let profileIndex = 1;
    return {
        version: config.version ?? "1.0",
        rebuildRequired: config.rebuildRequired,
        runData: config.runData,
        profiles: [
            ...config.pointToTriangle.groups.map((g) =>
                populateMissingData({
                    id: g.id ?? window.crypto.randomUUID(),
                    name: g.name ?? `Deviation ${profileIndex++}`,
                    copyFromProfileId: g.copyFromProfileId,
                    deviationType: DeviationType.PointToTriangle,
                    index: -1,
                    subprofiles: g.subprofiles ?? [
                        {
                            from: {
                                objectIds: g.objectIds,
                                groupIds: g.groupIds,
                            },
                            to: {
                                objectIds: [],
                                groupIds: [],
                            },
                            favorites: g.favorites ?? [],
                            legendGroups: [],
                        },
                    ],
                    colors: g.colors ?? defaultColors,
                    hasFromAndTo: g.subprofiles !== undefined,
                    fromAndToSwapped: false,
                    fixTriangleWinding: {
                        enabled: g.fixTriangleWinding?.enabled ?? false,
                        fixRadius: g.fixTriangleWinding?.fixRadius ?? 0,
                    },
                } as UiDeviationProfile),
            ),
            ...config.pointToPoint.groups.map((g) =>
                populateMissingData({
                    id: g.id ?? window.crypto.randomUUID(),
                    name: g.name ?? `Deviation ${profileIndex++}`,
                    copyFromProfileId: g.copyFromProfileId,
                    deviationType: DeviationType.PointToPoint,
                    index: -1,
                    subprofiles: g.subprofiles?.map((sp) => ({
                        ...sp,
                        from: g.fromAndToSwapped ? sp.to : sp.from,
                        to: g.fromAndToSwapped ? sp.from : sp.to,
                    })) ?? [
                        {
                            from: g.fromAndToSwapped ? g.to : g.from,
                            to: g.fromAndToSwapped ? g.from : g.to,
                            favorites: g.favorites ?? [],
                            legendGroups: [],
                        },
                    ],
                    colors: g.colors ?? defaultColors,
                    hasFromAndTo: true,
                    fromAndToSwapped: g.fromAndToSwapped,
                    fixTriangleWinding: {
                        enabled: g.fixTriangleWinding?.enabled ?? false,
                        fixRadius: g.fixTriangleWinding?.fixRadius ?? 0,
                    },
                } as UiDeviationProfile),
            ),
        ],
    };
}

function populateMissingData(profile: UiDeviationProfile): UiDeviationProfile {
    return {
        ...profile,
        subprofiles: profile.subprofiles.map((sp) => {
            return {
                ...sp,
                favorites: sp.favorites ?? [],
            };
        }),
    };
}

function matchProfileIndexes(current: UiDeviationConfig, calculated: DeviationProjectConfig) {
    const calcProfiles = [
        ...calculated.pointToTriangle.groups.map((g) => ({
            id: g.id,
            name: g.name,
        })),
        ...calculated.pointToPoint.groups.map((g) => ({
            id: g.id,
            name: g.name,
        })),
    ];

    const calculatedHasIds = calcProfiles.some((p) => p.id);

    current.profiles.forEach((profile, i) => {
        if (calculatedHasIds) {
            profile.index = calcProfiles.findIndex((p) => p.id === profile.id);
        } else {
            profile.index = i;
        }
    });
}
